import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { getMyInquiries } from "@/api/csApi";

interface InquiryImage {
  imageId: number;
  imageUrl: string;
}

// 💡 백엔드에서 넘어오는 데이터 타입 정의
interface InquiryAnswer {
  content: string;
  createdAt: string;
}

interface Inquiry {
  inquiryId: number;
  category: string;
  title: string;
  content: string;
  status: string; // "WAIT" | "DONE"
  createdAt: string;
  answer: InquiryAnswer | null;
  images: InquiryImage[]; // 💡 추가: 백엔드에서 넘어오는 이미지 목록
}

export default function InquiryListPage() {
  const navigate = useNavigate();
  const { isLoggedIn, isLoading: isAuthLoading } = useAuth();
  const hasAlerted = useRef(false); // 💡 알림창 중복 방지

  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);

  // 1. 비로그인 사용자 방어막 및 데이터 호출
  useEffect(() => {
    if (isAuthLoading) return;

    if (!isLoggedIn && !hasAlerted.current) {
      hasAlerted.current = true;
      alert("로그인이 필요한 서비스입니다.");
      navigate("/login", { replace: true });
      return;
    }

    // 로그인된 상태라면 백엔드에서 내역 불러오기
    if (isLoggedIn) {
      fetchInquiries();
    }
  }, [isLoggedIn, isAuthLoading, navigate]);

  // 2. 실제 데이터 패칭 함수
  const fetchInquiries = async () => {
    try {
      setIsLoadingData(true);
      const res = await getMyInquiries();
      if (res.success) {
        setInquiries(res.data);
      }
    } catch (error) {
      console.error("문의 내역을 불러오는데 실패했습니다.", error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const toggleInquiry = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  // 날짜 변환 헬퍼 (예: "2026-07-16T10:00:00" -> "2026-07-16")
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.substring(0, 10);
  };

  if (isAuthLoading || !isLoggedIn) return null;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />

      <main className="flex-1 mx-auto max-w-4xl px-4 py-12 lg:px-6 mb-20 w-full">
        {/* 상단 타이틀 */}
        <div className="mb-8 border-b border-slate-200 pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black tracking-tight text-slate-800">1:1 문의 내역</h2>
            <p className="mt-2 text-sm text-slate-500">
              고객님이 남겨주신 문의와 답변 내역을 확인하실 수 있습니다.
            </p>
          </div>
          <button
            onClick={() => navigate("/cs/inquiry")}
            className="shrink-0 h-11 rounded-lg bg-slate-900 px-6 text-sm font-bold text-white hover:bg-slate-800 transition-colors"
          >
            새 문의 작성하기
          </button>
        </div>

        {/* 문의 리스트 영역 */}
        <div className="border-t border-slate-800">
          {isLoadingData ? (
            <div className="py-20 text-center text-slate-500 font-medium">
              내역을 불러오는 중입니다...
            </div>
          ) : inquiries.length > 0 ? (
            inquiries.map((inq) => (
              <div key={inq.inquiryId} className="border-b border-slate-200">

                {/* 🔽 질문 헤더 영역 */}
                <button
                  onClick={() => toggleInquiry(inq.inquiryId)}
                  className="flex w-full items-center justify-between py-5 px-2 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {/* 상태값(WAIT/DONE)을 한글로 매핑하여 스타일 적용 */}
                    <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${
                      inq.status === "DONE"
                        ? "bg-indigo-50 text-indigo-700 border border-indigo-100"
                        : "bg-slate-100 text-slate-600 border border-slate-200"
                    }`}>
                      {inq.status === "DONE" ? "답변완료" : "답변대기"}
                    </span>
                    <span className="shrink-0 w-16 text-sm font-bold text-slate-500 md:w-20">[{inq.category}]</span>
                    <span className={`text-[15px] truncate ${openId === inq.inquiryId ? "font-bold text-slate-800" : "font-medium text-slate-700"}`}>
                      {inq.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className="hidden sm:inline text-xs text-slate-400">{formatDate(inq.createdAt)}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${openId === inq.inquiryId ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* 🔽 질문 상세 및 답변 영역 (열렸을 때만 표시) */}
                {openId === inq.inquiryId && (
                  <div className="bg-slate-50 border-t border-slate-100">
                    {/* 내 질문 내용 */}
                    <div className="px-6 py-6 text-[15px] text-slate-700 whitespace-pre-wrap leading-relaxed">
                      <div className="text-xs font-bold text-rose-500 mb-2">Q. 문의 내용</div>
                      {inq.content}

                      {inq.images && inq.images.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-4">
                          {inq.images.map((img) => (
                            <div
                              key={img.imageId}
                              className="h-24 w-24 md:h-32 md:w-32 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white cursor-zoom-in hover:opacity-90 transition-opacity shadow-sm"
                              onClick={() => window.open(img.imageUrl, "_blank")} // 클릭 시 원본 이미지 새 창 열기
                            >
                              <img
                                src={img.imageUrl}
                                alt="첨부 이미지"
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 관리자 답변 내용 (백엔드에서 answer 객체가 존재할 경우) */}
                    {inq.answer ? (
                      <div className="px-6 py-6 border-t border-slate-200/60 bg-indigo-50/30 text-[15px] text-slate-600 whitespace-pre-wrap leading-relaxed">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-indigo-600">A. MG Flower 답변</span>
                          <span className="text-xs text-slate-400">{formatDate(inq.answer.createdAt)}</span>
                        </div>
                        {inq.answer.content}
                      </div>
                    ) : (
                      <div className="px-6 py-6 border-t border-slate-200/60 bg-slate-100/50 text-sm text-slate-500 italic">
                        빠른 시일 내에 성심껏 답변해 드리겠습니다. 잠시만 기다려 주세요.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="py-20 text-center text-slate-500">
              남기신 1:1 문의 내역이 없습니다.
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}